import { useState, useCallback, useEffect } from 'preact/hooks'
import { uploadBlob, downloadBlobAsUrl, downloadBlobAsText } from '../../services/api'
import { FullscreenImageViewer } from '../FullscreenImageViewer'
import { FullscreenDocumentViewer } from '../FullscreenDocumentViewer'

/**
 * AttachmentType index that mirrors the mobile app's `AttachmentType` enum
 * (`pipar/lib/models/note_model.dart`):
 *
 *   0 = image, 1 = audio, 2 = document, 3 = other, 4 = pdf
 *
 * Kept in sync so attachments uploaded from the web open correctly when
 * the mobile app re-syncs the note.
 */
const ATT_TYPE_IMAGE = 0
const ATT_TYPE_DOCUMENT = 2
const ATT_TYPE_OTHER = 3
const ATT_TYPE_PDF = 4

/** Attachment record stored inside `note.attachments` / `task.attachments`. */
export interface StoredAttachment {
  id: string
  /** Cloud blob key (e.g. `<userhash>/notes/<note-id>/<filename>`). */
  blobKey: string
  /** snake_case mirror written for older readers / sql columns. */
  blob_key?: string
  /** Original filename — also written to filePath so mobile shows a label. */
  filePath: string
  /** Mobile's AttachmentType index. */
  type: number
  description?: string
  /** Stored MIME type so the open-fullscreen renderer doesn't have to sniff. */
  metadata?: { mime_type?: string; original_name?: string }
  createdAt: string
}

/** Pick the AttachmentType bucket from the file's MIME type. */
function attachmentTypeFor(mime: string, filename: string): number {
  if (mime.startsWith('image/')) return ATT_TYPE_IMAGE
  if (mime === 'application/pdf' || /\.pdf$/i.test(filename)) return ATT_TYPE_PDF
  if (mime.startsWith('text/') || /\.(md|markdown|txt|html?)$/i.test(filename)) return ATT_TYPE_DOCUMENT
  return ATT_TYPE_OTHER
}

/** Open a system file picker, upload each selected file as a blob, and append
 *  attachment records to the entity. Returns the updated attachments array. */
export async function pickAndUploadAttachments(
  entityType: 'notes' | 'tasks',
  entityId: string,
  acceptImages: boolean,
  acceptDocs: boolean,
): Promise<StoredAttachment[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    const accepts: string[] = []
    if (acceptImages) accepts.push('image/*')
    if (acceptDocs) accepts.push('.pdf,.txt,.md,.markdown,.html,.htm,application/pdf,text/plain,text/markdown,text/html')
    input.accept = accepts.join(',')
    input.onchange = async () => {
      const files = Array.from(input.files || [])
      if (files.length === 0) { resolve([]); return }
      const out: StoredAttachment[] = []
      for (const file of files) {
        try {
          const buf = await file.arrayBuffer()
          const bytes = new Uint8Array(buf)
          const mime = file.type || 'application/octet-stream'
          const result = await uploadBlob(bytes, file.name, mime, entityType, entityId)
          if (!result.ok || !result.key) {
            console.warn('Upload failed for', file.name, result.error)
            continue
          }
          out.push({
            id: crypto.randomUUID(),
            blobKey: result.key,
            blob_key: result.key,
            filePath: file.name,
            type: attachmentTypeFor(mime, file.name),
            metadata: { mime_type: mime, original_name: file.name },
            createdAt: new Date().toISOString(),
          })
        } catch (e) {
          console.warn('Failed to attach file', file.name, e)
        }
      }
      resolve(out)
    }
    input.click()
  })
}

interface AttachmentListProps {
  attachments: StoredAttachment[]
  onChange: (next: StoredAttachment[]) => void
  uploading: boolean
  onAddImages?: () => void
  onAddDocs?: () => void
}

/** UI for showing & managing the attachments list inside a note/task editor. */
export function AttachmentList({ attachments, onChange, uploading, onAddImages, onAddDocs }: AttachmentListProps) {
  const handleRemove = (id: string) => {
    onChange(attachments.filter((a) => a.id !== id))
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {onAddImages && (
          <button
            onClick={onAddImages}
            disabled={uploading}
            style={attachBtnStyle}
            title="Add an image from your computer"
          >
            {uploading ? '\u{2026}' : '\u{1F5BC}\u{FE0F}'} Add image
          </button>
        )}
        {onAddDocs && (
          <button
            onClick={onAddDocs}
            disabled={uploading}
            style={attachBtnStyle}
            title="Add a PDF, MD or TXT document from your computer"
          >
            {uploading ? '\u{2026}' : '\u{1F4C4}'} Add document
          </button>
        )}
      </div>
      {attachments.length === 0 ? (
        <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.4)' }}>
          No attachments
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {attachments.map((att) => (
            <AttachmentRow key={att.id} attachment={att} onRemove={() => handleRemove(att.id)} />
          ))}
        </div>
      )}
    </div>
  )
}

function AttachmentRow({ attachment, onRemove }: { attachment: StoredAttachment; onRemove: () => void }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageOpen, setImageOpen] = useState(false)
  const [docOpen, setDocOpen] = useState(false)
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [docContent, setDocContent] = useState<string | null>(null)

  const mime = attachment.metadata?.mime_type || ''
  const isImage = attachment.type === ATT_TYPE_IMAGE || mime.startsWith('image/')
  const isPdf = attachment.type === ATT_TYPE_PDF || mime === 'application/pdf'
  const isText = attachment.type === ATT_TYPE_DOCUMENT || mime.startsWith('text/')

  // Lazy-load the image as a thumbnail; documents fetch on demand when the
  // user clicks "Open".
  useEffect(() => {
    if (!isImage) return
    let cancelled = false
    downloadBlobAsUrl(attachment.blobKey).then((res) => {
      if (cancelled || !res) return
      setImageUrl(res.url)
    }).catch(() => { /* ignore */ })
    return () => { cancelled = true }
  }, [attachment.blobKey, isImage])

  const handleOpen = useCallback(async () => {
    if (isImage) {
      if (imageUrl) setImageOpen(true)
      else {
        const res = await downloadBlobAsUrl(attachment.blobKey)
        if (res) {
          setImageUrl(res.url)
          setImageOpen(true)
        }
      }
      return
    }
    if (isPdf) {
      const res = await downloadBlobAsUrl(attachment.blobKey)
      if (res) {
        setDocUrl(res.url)
        setDocOpen(true)
      }
      return
    }
    // Text/markdown/html document — load text and either render markdown→HTML
    // or surface as plain text via a Blob URL.
    const text = await downloadBlobAsText(attachment.blobKey)
    if (text) {
      const fileName = attachment.filePath || ''
      const isMarkdown = mime === 'text/markdown' || /\.(md|markdown)$/i.test(fileName)
      const html = isMarkdown ? markdownToHtml(text, fileName) : (mime.includes('html') || /\.(html?|htm)$/i.test(fileName)) ? text : `<pre style="font-family:ui-monospace,monospace;white-space:pre-wrap;padding:24px">${text.replace(/[<&>]/g, (c) => ({'<':'&lt;','>':'&gt;','&':'&amp;'} as Record<string, string>)[c])}</pre>`
      const blob = new Blob([html], { type: 'text/html' })
      setDocContent(URL.createObjectURL(blob))
      setDocOpen(true)
    }
  }, [attachment, isImage, isPdf, imageUrl, mime])

  const closeDoc = () => {
    setDocOpen(false)
    if (docUrl) { URL.revokeObjectURL(docUrl); setDocUrl(null) }
    if (docContent) { URL.revokeObjectURL(docContent); setDocContent(null) }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: 8,
      background: 'rgba(0,51,68,0.6)',
      border: '1px solid rgba(250,250,250,0.1)',
      borderRadius: 6,
    }}>
      {isImage && imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          onClick={handleOpen}
          style={{ width: 48, height: 48, borderRadius: 4, objectFit: 'cover', cursor: 'zoom-in' }}
          title="Open"
        />
      ) : (
        <span
          onClick={handleOpen}
          style={{ fontSize: 28, width: 48, textAlign: 'center', cursor: 'pointer' }}
          title="Open"
        >
          {isPdf ? '\u{1F4C4}' : isText ? '\u{1F4DD}' : '\u{1F4CE}'}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {attachment.filePath || 'Attachment'}
        </div>
        {mime && <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.45)' }}>{mime}</div>}
      </div>
      <button
        onClick={handleOpen}
        style={{ ...attachBtnStyle, fontSize: 11 }}
      >Open</button>
      <button
        onClick={onRemove}
        style={{ ...attachBtnStyle, fontSize: 11, color: 'rgba(250,250,250,0.5)' }}
        title="Remove from this entity (the blob stays in cloud storage)"
      >Remove</button>
      {imageOpen && imageUrl && (
        <FullscreenImageViewer src={imageUrl} alt={attachment.filePath} onClose={() => setImageOpen(false)} />
      )}
      {docOpen && (docUrl || docContent) && (
        <FullscreenDocumentViewer
          src={(docUrl || docContent) as string}
          mimeType={isPdf ? 'application/pdf' : 'text/html'}
          name={attachment.filePath}
          onClose={closeDoc}
        />
      )}
    </div>
  )
}

const attachBtnStyle: Record<string, string | number> = {
  padding: '4px 10px',
  fontSize: 12,
  border: '1px solid rgba(45,165,160,0.4)',
  borderRadius: 6,
  background: 'rgba(45,165,160,0.1)',
  color: '#2DA5A0',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

/** Tiny markdown→HTML used for previewing attached `.md` files. Trimmed copy
 *  of the renderer in `FilesView.tsx` — kept local so this control doesn't
 *  reach into that module. */
function markdownToHtml(md: string, title: string): string {
  let html = md
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _l, code) => {
      const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return `<pre><code>${escaped}</code></pre>`
    })
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
    .replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
    .replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
  html = html.split('\n\n').map((b) => {
    const t = b.trim()
    if (!t) return ''
    if (/^<(h[1-6]|ul|ol|li|pre|blockquote|hr|table|div|p)/.test(t)) return t
    return `<p>${t}</p>`
  }).join('\n')
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;line-height:1.6;max-width:900px;margin:0 auto;padding:24px;color:#1a1a1a}h1,h2,h3{color:#2c3e50}pre{background:#f4f4f4;padding:12px;border-radius:4px;overflow:auto}code{background:#f4f4f4;padding:2px 6px;border-radius:3px}pre code{background:none;padding:0}</style></head><body>${html}</body></html>`
}
